import { createGuestRequest } from '../features/UnauthRequest/api/guestRequestApi'
import { createRequest, createOffer } from '../features/Request/api/requestApi'
import { createContract, createBill } from '../features/Contract/api/contractApi'
import { type GuestRequest } from '../features/UnauthRequest/utils/types'
import { type Request, type Offer } from '../features/Request/utils/types'
import { type Contract, type Bill } from '../features/Contract/utils/types'
import { v1Api } from './generatedClient'
import type { APIUnreadOverview } from '../generated-types/models/APIUnreadOverview'
import type { APIUnreadEntity } from '../generated-types/models/APIUnreadEntity'
import type { APIEntityEnum } from '../generated-types/models/APIEntityEnum'
import type { V1SearchUnreadMarkReadCreateRequest } from '../generated-types/apis/V1Api'

type UnreadCollectionRaw =
  | APIUnreadEntity
  | {
      count?: number | null
      items?: any[]
    }
  | null
  | undefined

export interface UnreadCollection<T> {
  count: number
  items: T[]
}

export interface UnreadOverview {
  guestRequests: UnreadCollection<GuestRequest>
  requests: UnreadCollection<Request>
  offers: UnreadCollection<Offer>
  contracts: UnreadCollection<Contract>
  bills: UnreadCollection<Bill>
  total: number
}

export type UnreadEntityType = 'guest_request' | 'request' | 'offer' | 'contract' | 'bill'

function mapCollection<T>(
  raw: UnreadCollectionRaw,
  transform: (item: any) => T
): UnreadCollection<T> {
  const rawItems = Array.isArray(raw?.items) ? raw.items : []
  const items = rawItems.map(transform)
  const count = typeof raw?.count === 'number' ? raw.count : items.length
  return { count, items }
}

export async function fetchUnreadOverview(): Promise<UnreadOverview> {
  const response: APIUnreadOverview = await v1Api.v1SearchUnreadOverviewRetrieve()

  const guestRequests = mapCollection(response.guestRequests, createGuestRequest)
  const requests = mapCollection(response.requests, createRequest)
  const offers = mapCollection(response.offers, createOffer)
  const contracts = mapCollection(response.contracts, createContract)
  const bills = mapCollection(response.bills, createBill)

  return {
    guestRequests,
    requests,
    offers,
    contracts,
    bills,
    total: typeof response.total === 'number'
      ? response.total
      : guestRequests.count +
        requests.count +
        offers.count +
        contracts.count +
        bills.count,
  }
}

export async function markEntityAsRead(entity: UnreadEntityType, id: number): Promise<void> {
  const requestPayload: V1SearchUnreadMarkReadCreateRequest = {
    aPIMarkReadRequestRequest: {
      entity: entity as APIEntityEnum,
      id,
    },
  }

  await v1Api.v1SearchUnreadMarkReadCreate(requestPayload)
}
